import { useState, useEffect, useCallback, useRef } from 'react';
import { alertApi, AlertData, AlertFilters, CreateAlertRequest, UpdateAlertRequest, AlertListResponse } from '../services/alertApi';
import { SimplifiedCreateAlertRequest, SimplifiedCreateAlertResponse } from '../types/alerts';
import { apiService, ApiResponse } from '../services/api';

export const useAlerts = () => {
  const [alerts, setAlerts] = useState<AlertData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize] = useState(5);
  const currentPageRef = useRef(currentPage);
  
  // Keep ref in sync with state
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  // Define type for apiAlert
  const loadUserAlerts = useCallback(async (userId: number, filters: AlertFilters = {}, page: number = 1, pageSize: number = 5) => {
    console.log('loadUserAlerts called with userId:', userId, 'filters:', filters, 'page:', page, 'pageSize:', pageSize);
    setLoading(true);
    setError(null);

    try {
      
      const response: ApiResponse<AlertListResponse> = await apiService.request<AlertListResponse>(`/alert/user/${userId}`);
      if (!response.data || !response.data.alerts) {
        throw new Error('Failed to fetch user alerts');
      }

      const transformedAlerts = response.data.alerts.map((apiAlert: AlertData) => ({
        id: apiAlert.alertId,
        alertName: apiAlert.alertName,
        deviceName: apiAlert.deviceName,
        conditions: apiAlert.alertConditions.map(cond => cond.parameterName).join(', '),
        recipients: apiAlert.alertRecipients.map(rec => rec.email).join(', '),
        status: (apiAlert.isActive ? 'active' : 'inactive') as 'active' | 'inactive',
        lastTriggered: apiAlert.lastTriggeredAt || 'Never',
        alertConditions: apiAlert.alertConditions || [],
        alertRecipients: apiAlert.alertRecipients || [],
        alertId: apiAlert.alertId,
        description: apiAlert.description,
        deviceId: apiAlert.deviceId,
        createdByUserId: apiAlert.createdByUserId,
        createdByUserName: apiAlert.createdByUserName,
        isActive: apiAlert.isActive,
        createdAt: apiAlert.createdAt,
        updatedAt: apiAlert.updatedAt,
        lastTriggeredAt: apiAlert.lastTriggeredAt,
        triggerCount: apiAlert.triggerCount
      }));

      setAlerts(transformedAlerts);
      setTotalCount(response.data.totalCount);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching user alerts:', error);
      setError('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  // Simplified alert creation - new simplified API
  const createSimplifiedAlert = useCallback(async (alertData: SimplifiedCreateAlertRequest): Promise<SimplifiedCreateAlertResponse | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await alertApi.createSimplifiedAlert(alertData);
      await loadUserAlerts(JSON.parse(localStorage.getItem('userData') || '{}').userId, {}, 1); // Reload alerts after creation, go to page 1
      return response;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create alert');
      console.error('Error creating simplified alert:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [loadUserAlerts]);

  // Create new alert
  const createAlert = useCallback(async (alertData: CreateAlertRequest): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      await alertApi.createAlert(alertData);
      await loadUserAlerts(JSON.parse(localStorage.getItem('userData') || '{}').userId, {}, 1); // Reload alerts after creation, go to page 1
      return true;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create alert');
      console.error('Error creating alert:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [loadUserAlerts]);

  // Update alert
  const updateAlert = useCallback(async (alertId: number, alertData: UpdateAlertRequest): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Updating alert with ID:', alertId, 'and data:', alertData);
      await alertApi.updateAlert(alertId, alertData);
      // Force reload alerts to get the latest data
      await loadUserAlerts(JSON.parse(localStorage.getItem('userData') || '{}').userId, {}, currentPageRef.current);
      
      return true;
    } catch (err: any) {
      console.error('Error updating alert:', err);
      setError(err.response?.data?.message || 'Failed to update alert');
      return false;
    } finally {
      setLoading(false);
    }
  }, [loadUserAlerts]);

  // Simplified alert update - new simplified API
  const updateSimplifiedAlert = useCallback(async (alertId: number, alertData: SimplifiedCreateAlertRequest): Promise<SimplifiedCreateAlertResponse | null> => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Updating simplified alert with ID:', alertId, 'and data:', alertData);
      const response = await alertApi.updateSimplifiedAlert(alertId, alertData);
      // Force reload alerts to get the latest data
      await loadUserAlerts(JSON.parse(localStorage.getItem('userData') || '{}').userId, {}, currentPageRef.current);
      
      return response;
    } catch (err: any) {
      console.error('Error updating simplified alert:', err);
      setError(err.response?.data?.message || 'Failed to update alert');
      return null;
    } finally {
      setLoading(false);
    }
  }, [loadUserAlerts]);

  // Delete alert
  const deleteAlert = useCallback(async (alertId: number): Promise<boolean> => {
    console.log('useAlerts deleteAlert called with alertId:', alertId);
    setLoading(true);
    setError(null);
    
    try {
      console.log('Calling alertApi.deleteAlert...');
      await alertApi.deleteAlert(alertId);
      // Optimistically remove the alert from the current list
      setAlerts(prevAlerts => prevAlerts.filter(alert => alert.alertId !== alertId));
      
      // Update total count and pages after deletion
      setTotalCount(prevCount => Math.max(0, prevCount - 1));
      setTotalPages(() => Math.ceil(Math.max(0, totalCount - 1) / pageSize));
      
      // If we're on a page that no longer exists, go to the last page
      const newTotalPages = Math.ceil(Math.max(0, totalCount - 1) / pageSize);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      }
      return true;
    } catch (err: any) {
      console.error('Error in deleteAlert:', err);
      setError(err.response?.data?.message || 'Failed to delete alert');
      return false;
    } finally {
      setLoading(false);
    }
  }, [currentPage, totalCount, pageSize]);

  // Toggle alert status
  const toggleAlertStatus = useCallback(async (alertId: number): Promise<{ success: boolean; isActive?: boolean }> => {
    setError(null);
    
    try {
      console.log('useAlerts toggleAlertStatus called with alertId:', alertId);
      const result = await alertApi.toggleAlertStatus(alertId);
      console.log('toggleAlertStatus result:', result);
      
      // Optimistically update the alert status in the current list
      setAlerts(prevAlerts => prevAlerts.map(alert => 
        alert.alertId === alertId 
          ? { ...alert, isActive: result.isActive }
          : alert
      ));
      
      console.log('Alert status updated in local state');
      return { success: true, isActive: result.isActive };
    } catch (err: any) {
      console.error('Error in toggleAlertStatus:', err);
      setError(err.message || 'Failed to toggle alert status');
      return { success: false };
    } 
  }, []);

  // Set alert status directly
  const setAlertStatus = useCallback(async (alertId: number, isActive: boolean): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('useAlerts setAlertStatus called with alertId:', alertId, 'isActive:', isActive);
      const result = await alertApi.setAlertStatus(alertId, isActive);
      console.log('setAlertStatus result:', result);
      
      // Optimistically update the alert status in the current list
      setAlerts(prevAlerts => prevAlerts.map(alert => 
        alert.alertId === alertId 
          ? { ...alert, isActive: result.isActive }
          : alert
      ));
      
      console.log('Alert status updated in local state');
      return true;
    } catch (err: any) {
      console.error('Error in setAlertStatus:', err);
      setError(err.message || 'Failed to update alert status');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Send test alert
  const sendTestAlert = useCallback(async (alertId: number): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      await alertApi.sendTestAlert(alertId);
      return true;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send test alert');
      console.error('Error sending test alert:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get alert by ID
  const getAlertById = useCallback(async (alertId: number): Promise<AlertData | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const alert = await alertApi.getAlertById(alertId);
      return alert;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to get alert');
      console.error('Error getting alert:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get alerts by device
  const getAlertsByDevice = useCallback(async (deviceId: number): Promise<AlertData[]> => {
    setLoading(true);
    setError(null);
    
    try {
      const deviceAlerts = await alertApi.getAlertsByDevice(deviceId);
      return deviceAlerts;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to get device alerts');
      console.error('Error getting device alerts:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Process all alerts (Admin only)
  const processAllAlerts = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      await alertApi.processAllAlerts();
      return true;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to process alerts');
      console.error('Error processing alerts:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Manual refresh function
  const refreshAlerts = useCallback(async () => {
    console.log('Manual refresh of alerts triggered');
    await loadUserAlerts(JSON.parse(localStorage.getItem('userData') || '{}').userId, {}, currentPageRef.current);
  }, [loadUserAlerts]);

  // Go to specific page
  const goToPage = useCallback((page: number) => {
    setCurrentPage(page);
    loadUserAlerts(JSON.parse(localStorage.getItem('userData') || '{}').userId, {}, page);
  }, [loadUserAlerts]);

  // Restore the useEffect that calls loadUserAlerts on component mount
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const userId = userData.userId;
    if (userId) {
      loadUserAlerts(userId);
    }
  }, []);

  return {
    // State
    alerts,
    loading,
    error,
    totalCount,
    currentPage,
    totalPages,
    pageSize,
    
    // Actions
    createSimplifiedAlert,
    createAlert,
    updateAlert,
    updateSimplifiedAlert,
    deleteAlert,
    toggleAlertStatus,
    setAlertStatus,
    sendTestAlert,
    getAlertById,
    getAlertsByDevice,
    processAllAlerts,
    clearError,
    goToPage,
    refreshAlerts
  };
};
